import Button from "@material-ui/core/Button"
import ButtonGroup from "@material-ui/core/ButtonGroup"
import List from "@material-ui/core/List"
import ListItem from "@material-ui/core/ListItem"
import ListItemText from "@material-ui/core/ListItemText"
import ListItemSecondaryAction from "@material-ui/core/ListItemSecondaryAction"
import Typography from "@material-ui/core/Typography"
import { makeStyles } from "@material-ui/core"
import { COLOR_DARK_100, COLOR_WHITE } from "../../constants"
import { Check } from "react-feather"
import { entries } from "lodash"
import useTranslations, { Translations } from "./use-translations"

const redundantAbilityMap = {
  UpdateAnyCustomer: "CreateCustomers",
  UpdateAnySupplier: "CreateSuppliers",
  UpdateAnyWorkspace: "CreateWorkspaces",
  UpdateAnyMembershipPermissions: "CreateMemberships",
  UpdateProjectPermissions: "UpdateAnyMembershipPermissions",
} as const

export default function AbilitiesTable({ groupedAbilities, onUpdateAbilities }: AbilitiesTableProps) {
  const translations = useTranslations()
  return (
    <>
      {groupedAbilities.map(({ resource, abilities }) => {
        return (
          <AbilityRowGroup
            key={resource}
            resource={resource}
            abilities={abilities}
            onUpdateAbilities={onUpdateAbilities}
            translations={translations}
          />
        )
      })}
    </>
  )
}

const AbilityRowGroup = ({ resource, abilities, onUpdateAbilities, translations }: AbilityRowGroupProps) => {
  const groupHeader = translations[getHeaderKey(resource)] || resource
  const abilityRows = makeAbilityRows(abilities)
  const onEnableAbility = (ability: string) => {
    onUpdateAbilities({ abilitiesToAdd: [ability], abilitiesToRemove: [] })
  }
  const onDisableAbility = (ability: string) => {
    onUpdateAbilities({ abilitiesToRemove: [ability], abilitiesToAdd: [] })
  }

  return (
    <>
      <Typography variant="subtitle1" style={{ marginLeft: 16 }}>
        {groupHeader}
      </Typography>
      <List>
        {abilityRows.map(({ id, description, yesNoState }) => {
          const onEnable = () => onEnableAbility(id)
          const onDisable = () => onDisableAbility(id)

          return (
            <AbilityRow
              key={id}
              description={description}
              yesNoState={yesNoState}
              onEnable={onEnable}
              onDisable={onDisable}
            />
          )
        })}
      </List>
    </>
  )

  // converts ability map to ability rows
  // for example { CanRead: true } => { id: 'CanRead', description: 'Can read', isEnabled: true }
  function makeAbilityRows(abilities: { [id: string]: boolean } | { [id: string]: boolean }[]) {
    const adaptedAbilities = adaptAbilities(abilities)
    return entries(adaptedAbilities)
      .filter(([abilityName]) => !redundantAbilityMap[abilityName as keyof typeof redundantAbilityMap])
      .map(([id, yesNoState]) => {
        const description = translations[getDescriptionKey(id)] || id
        return {
          id,
          description,
          yesNoState,
        }
      })
  }
}

const AbilityRow = ({
  description,
  onEnable,
  onDisable,
  yesNoState,
}: {
  description: string
  yesNoState: 0 | 1 | 2
  onEnable: () => void
  onDisable: () => void
}) => {
  const translations = useTranslations()
  const classes = useStyles()
  const isYesEnabled = yesNoState === 1 || yesNoState === 2
  const isNoEnabled = yesNoState === 0 || yesNoState === 2
  const yesBtnColor = yesNoState === 2 ? "secondary" : "primary"
  const noBtnColor = yesNoState === 2 ? "secondary" : "primary"
  const yesBtnVariant = isYesEnabled ? "contained" : "outlined"
  const noBtnVariant = isNoEnabled ? "contained" : "outlined"
  const yesBtnIcon = isYesEnabled ? <Check size={16} /> : null
  const noBtnIcon = isNoEnabled ? <Check size={16} /> : null

  return (
    <ListItem divider style={{ paddingRight: 148 }}>
      <ListItemText primary={description} />
      <ListItemSecondaryAction>
        <ButtonGroup variant="outlined" size="small">
          <Button
            color={yesBtnColor}
            variant={yesBtnVariant}
            startIcon={yesBtnIcon}
            className={classes.button}
            onClick={onEnable}
          >
            {translations.yesBtnLabel}
          </Button>
          <Button
            color={noBtnColor}
            variant={noBtnVariant}
            startIcon={noBtnIcon}
            className={classes.button}
            onClick={onDisable}
          >
            {translations.noBtnLabel}
          </Button>
        </ButtonGroup>
      </ListItemSecondaryAction>
    </ListItem>
  )
}

const useStyles = makeStyles(() => ({
  button: {
    minWidth: "64px",
    "&.MuiButton-outlinedPrimary": {
      color: COLOR_DARK_100,
      borderColor: COLOR_DARK_100,
    },
    "&.MuiButton-containedPrimary": {
      backgroundColor: COLOR_DARK_100,
    },
    "&.MuiButton-containedSecondary": {
      backgroundColor: "#696969",
      "&:not(:last-child)": {
        borderRight: `2px solid ${COLOR_WHITE}`,
      },
    },
  },
}))

type RoleAbilitiesUpdateData = {
  abilitiesToAdd?: string[]
  abilitiesToRemove?: string[]
}

type AbilityRowGroupProps = {
  resource: string
  abilities: { [id: string]: boolean } | { [id: string]: boolean }[]
  onUpdateAbilities: (update: RoleAbilitiesUpdateData) => void
  translations: Translations
}
const getHeaderKey = (resource: string) => {
  return `${resource}PermissionsHeader` as keyof Translations
}
const getDescriptionKey = (id: string) => {
  return `${id}Description` as keyof Translations
}

type AbilitiesTableProps = {
  groupedAbilities: GroupedAbilities[]
  onUpdateAbilities: (updateData: UpdateData) => void
}

type GroupedAbilities = {
  resource: string
  abilities: { [ability: string]: boolean } | { [ability: string]: boolean }[]
}

type UpdateData = {
  abilitiesToAdd?: string[]
  abilitiesToRemove?: string[]
}

// Adapt abilities so boolean values are converted to 0, 1, 2
// 0 = false, 1 = true, 2 = mixed
function adaptAbilities(abilities: { [id: string]: boolean } | { [id: string]: boolean }[]) {
  const adapted: { [id: string]: 0 | 1 | 2 } = {}
  if (Array.isArray(abilities)) {
    abilities.forEach((item) => {
      Object.entries(item).forEach(([key, value]) => {
        if (adapted[key] === undefined) {
          adapted[key] = value ? 1 : 0
        } else if (adapted[key] === 1 && !value) {
          adapted[key] = 2
        } else if (adapted[key] === 0 && value) {
          adapted[key] = 2
        }
      })
    })
  } else {
    for (const key in abilities) {
      adapted[key] = abilities[key] ? 1 : 0
    }
  }
  return adapted
}
